/** Читалка канонического формата. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import type { Chapter, Manifest, ReadingState } from '../api'
import { BlockView } from '../reader/blocks'
import { PositionPusher, progressAt } from '../reader/position'
import {
  applySettings,
  DEFAULT_SETTINGS,
  loadSettings,
  saveSettings,
  type ReaderSettings,
  type Theme,
} from '../reader/settings'
import { Spinner } from '../components/Spinner'

// Насколько дальше должно быть прочитано на другом устройстве, чтобы
// предлагать переход. Меньший разрыв — обычный шум от округления.
const JUMP_THRESHOLD = 0.01

export function ReaderPage() {
  const { id } = useParams()
  const bookId = Number(id)
  const navigate = useNavigate()

  const [manifest, setManifest] = useState<Manifest | null>(null)
  const [chapter, setChapter] = useState<Chapter | null>(null)
  const [chapterIndex, setChapterIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [settings, setSettings] = useState<ReaderSettings>(loadSettings)
  const [panel, setPanel] = useState<'toc' | 'settings' | null>(null)
  const [barHidden, setBarHidden] = useState(false)
  const [currentBlock, setCurrentBlock] = useState(0)
  const [elsewhere, setElsewhere] = useState<ReadingState | null>(null)

  const pusher = useMemo(() => new PositionPusher(bookId), [bookId])
  const blockRefs = useRef<Map<number, HTMLElement>>(new Map())
  const pageRef = useRef<HTMLDivElement>(null)
  // Блок, к которому надо прокрутить сразу после отрисовки главы.
  const pendingScroll = useRef<number | null>(null)
  const lastScrollY = useRef(0)

  useEffect(() => applySettings(settings), [settings])

  // ---- начальная загрузка: манифест + сохранённая позиция ----
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [loaded, book] = await Promise.all([api.manifest(bookId), api.book(bookId)])
        if (cancelled) return
        setManifest(loaded)

        const state = book.reading
        const startChapter = state?.chapter ?? 0
        pendingScroll.current = state?.block ?? 0
        setChapterIndex(Math.min(startChapter, Math.max(0, loaded.chapters.length - 1)))

        // Если на другом устройстве прочитано заметно дальше — предложим перейти,
        // но не перенесём молча: пользователь мог намеренно вернуться назад.
        if (
          state &&
          state.max_progress > state.progress + JUMP_THRESHOLD &&
          state.max_chapter !== undefined
        ) {
          setElsewhere(state)
        }
      } catch (exc) {
        if (!cancelled) setError(describe(exc))
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [bookId])

  // ---- загрузка текущей главы ----
  useEffect(() => {
    if (!manifest) return
    let cancelled = false
    setChapter(null)
    api
      .chapter(bookId, chapterIndex)
      .then((loaded) => {
        if (cancelled) return
        blockRefs.current.clear()
        setChapter(loaded)
      })
      .catch((exc) => {
        if (!cancelled) setError(describe(exc))
      })
    return () => {
      cancelled = true
    }
  }, [bookId, chapterIndex, manifest])

  // ---- прокрутка к нужному блоку после отрисовки ----
  useEffect(() => {
    if (!chapter) return
    const target = pendingScroll.current
    pendingScroll.current = null
    if (target === null) {
      window.scrollTo({ top: 0 })
      return
    }
    // Эффект выполняется уже после обновления DOM, поэтому элементы главы
    // существуют и имеют размеры — ждать кадра не нужно.
    const element = blockRefs.current.get(target)
    if (element) element.scrollIntoView({ block: 'start' })
    else window.scrollTo({ top: 0 })
  }, [chapter])

  // ---- отслеживание видимого блока ----
  useEffect(() => {
    if (!chapter || !manifest) return
    let frame = 0

    const measure = () => {
      const block = topmostVisibleBlock(blockRefs.current)
      if (block === null) return
      setCurrentBlock(block)
      pusher.update(chapterIndex, block, progressAt(manifest, chapterIndex, block))
    }

    const onScroll = () => {
      // Панель прячется при прокрутке вниз и возвращается при движении вверх.
      const y = window.scrollY
      setBarHidden(y > 120 && y > lastScrollY.current)
      lastScrollY.current = y

      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    // Первое измерение — сразу, а не через кадр: в фоновой вкладке кадры не
    // отрисовываются, и позиция открытой главы иначе никогда бы не сохранилась.
    measure()
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [chapter, chapterIndex, manifest, pusher])

  // ---- отправка позиции при уходе ----
  useEffect(() => {
    const flush = () => pusher.flush()
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onHidden)
      flush()
    }
  }, [pusher])

  const goToChapter = useCallback((index: number, block = 0) => {
    pendingScroll.current = block
    setChapterIndex(index)
    setPanel(null)
  }, [])

  // ---- клавиатура ----
  useEffect(() => {
    if (!manifest) return
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        if (chapterIndex < manifest.chapters.length - 1) goToChapter(chapterIndex + 1)
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        if (chapterIndex > 0) goToChapter(chapterIndex - 1)
      } else if (event.key === 'Escape') {
        setPanel(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [chapterIndex, goToChapter, manifest])

  const update = (patch: Partial<ReaderSettings>) => {
    const next = { ...settings, ...patch }
    setSettings(next)
    saveSettings(next)
  }

  if (error) {
    return (
      <div className="center">
        <div>
          <p className="error">{error}</p>
          <Link className="btn" to={`/book/${bookId}`}>
            К карточке книги
          </Link>
        </div>
      </div>
    )
  }

  if (!manifest) return <Spinner />

  const progress = progressAt(manifest, chapterIndex, currentBlock)
  const info = manifest.chapters[chapterIndex]
  const isLast = chapterIndex >= manifest.chapters.length - 1

  return (
    <div className="reader">
      <div className={`reader__bar${barHidden ? ' reader__bar--hidden' : ''}`}>
        <button className="btn btn--ghost" onClick={() => navigate(`/book/${bookId}`)}>
          ← Назад
        </button>
        <div className="reader__title">
          {manifest.title}
          {info?.title ? ` · ${info.title}` : ''}
        </div>
        <button className="btn btn--ghost" onClick={() => setPanel('toc')}>
          Оглавление
        </button>
        <button className="btn btn--ghost" onClick={() => setPanel('settings')}>
          Вид
        </button>
      </div>

      <div className="reader__page" ref={pageRef}>
        {!chapter ? (
          <Spinner />
        ) : (
          <>
            {chapter.blocks.map((block) => (
              <div
                key={block.i}
                data-block={block.i}
                ref={(element) => {
                  if (element) blockRefs.current.set(block.i, element)
                  else blockRefs.current.delete(block.i)
                }}
              >
                <BlockView block={block} bookId={bookId} />
              </div>
            ))}
            <div className="reader__nav">
              <button
                className="btn"
                disabled={chapterIndex === 0}
                onClick={() => goToChapter(chapterIndex - 1)}
              >
                ← Предыдущая
              </button>
              <button
                className="btn"
                disabled={isLast}
                onClick={() => goToChapter(chapterIndex + 1)}
              >
                Следующая →
              </button>
            </div>
          </>
        )}
      </div>

      <div className="reader__progress">
        <span style={{ width: `${(progress * 100).toFixed(2)}%` }} />
      </div>

      {panel && <div className="overlay" onClick={() => setPanel(null)} />}

      {panel === 'toc' && (
        <div className="panel">
          <h3>Оглавление</h3>
          {manifest.chapters.map((item) => (
            <button
              key={item.index}
              className={`toc__item${item.index === chapterIndex ? ' toc__item--active' : ''}`}
              onClick={() => goToChapter(item.index)}
            >
              {item.title || `Глава ${item.index + 1}`}
            </button>
          ))}
        </div>
      )}

      {panel === 'settings' && (
        <div className="panel">
          <h3>Вид</h3>
          <div className="panel__row">
            <span>Тема</span>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {(['dark', 'sepia', 'light'] as Theme[]).map((theme) => (
                <button
                  key={theme}
                  className={`btn${settings.theme === theme ? ' btn--primary' : ''}`}
                  onClick={() => update({ theme })}
                >
                  {theme === 'dark' ? 'Тёмная' : theme === 'sepia' ? 'Сепия' : 'Светлая'}
                </button>
              ))}
            </div>
          </div>
          <Slider
            label="Размер шрифта"
            value={settings.fontSize}
            min={14}
            max={30}
            step={1}
            suffix="px"
            onChange={(fontSize) => update({ fontSize })}
          />
          <Slider
            label="Межстрочный интервал"
            value={settings.lineHeight}
            min={1.2}
            max={2.2}
            step={0.05}
            onChange={(lineHeight) => update({ lineHeight })}
          />
          <Slider
            label="Ширина колонки"
            value={settings.width}
            min={26}
            max={60}
            step={1}
            suffix="rem"
            onChange={(width) => update({ width })}
          />
          <div className="panel__row">
            <button className="btn" onClick={() => update(DEFAULT_SETTINGS)}>
              Сбросить
            </button>
            <button className="btn" onClick={() => setPanel(null)}>
              Готово
            </button>
          </div>
        </div>
      )}

      {elsewhere && (
        <div className="toast">
          <span>
            На другом устройстве прочитано до {Math.round(elsewhere.max_progress * 100)} %.
          </span>
          <button
            className="btn btn--primary"
            onClick={() => {
              goToChapter(elsewhere.max_chapter ?? 0, elsewhere.max_block ?? 0)
              setElsewhere(null)
            }}
          >
            Перейти
          </button>
          <button className="btn btn--ghost" onClick={() => setElsewhere(null)}>
            Остаться
          </button>
        </div>
      )}
    </div>
  )
}

/** Верхний блок, который ещё виден на экране. */
function topmostVisibleBlock(refs: Map<number, HTMLElement>): number | null {
  let best: number | null = null
  let bestTop = Number.NEGATIVE_INFINITY
  for (const [index, element] of refs) {
    const rect = element.getBoundingClientRect()
    // Ищем последний блок, начало которого ещё не ушло за верх экрана.
    if (rect.top <= 80 && rect.top > bestTop) {
      bestTop = rect.top
      best = index
    }
  }
  if (best !== null) return best
  // Ничего не прокручено — считаем текущим самый первый блок.
  let first: number | null = null
  for (const index of refs.keys()) {
    if (first === null || index < first) first = index
  }
  return first
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  suffix = '',
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix?: string
  onChange: (value: number) => void
}) {
  return (
    <div style={{ padding: '0.5rem 0' }}>
      <div className="panel__row" style={{ padding: 0 }}>
        <span>{label}</span>
        <span className="dim">
          {value}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ width: '100%' }}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  )
}

function describe(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return 'Не удалось загрузить книгу'
}
